import {
  AHP_PROGRAM_NAME,
  BOND_PROGRAM_NAME,
  CITC_PROGRAM_NAME,
  DEFERRED_LOAN_PROGRAM_NAME,
  HOME_PROGRAM_NAME,
  HTF_PROGRAM_NAME,
  HUD_PROGRAM_NAME,
  KCMO_PROGRAM_NAME,
  LIHTC_PROGRAM_NAME,
  NOAH_PROGRAM_NAME,
  NSP_PROGRAM_NAME,
  PBV_PROGRAM_NAME,
  PH_PROGRAM_NAME,
  RD_PROGRAM_NAME,
  ST_LOUIS_FUNDS_PROGRAM_NAME,
  TCAP_PROGRAM_NAME,
  VHCB_PROGRAM_NAME,
} from './constants';

export interface IProgramHelper {
  // programs
  // federal
  isHUD: boolean;
  isRD: boolean;
  isPH: boolean;
  isPBV: boolean;

  // state
  isHOME: boolean;
  isCITC: boolean;
  isLIHTC: boolean;
  isNOAH: boolean;
  isDeferredLoan: boolean;
  isVHCB: boolean;
  isAHP: boolean;
  isNSP: boolean;
  isKCMO: boolean;
  isStLouisFunds: boolean;
  isHTF: boolean;
  isTCAP: boolean;
  isBOND: boolean;

  // utility
  isNotHUDProgram: boolean;
  isLayered: boolean;
  programs: Array<string>;

  +some: () => boolean;
}

/**
 * Helper class to centralize program checking, and other assorted program related functions
 */
export class ProgramHelper {
  name: string = '';

  // programs
  // federal
  isHUD: boolean = false;
  isRD: boolean = false;
  isPH: boolean = false;
  isPBV: boolean = false;

  // state
  isHOME: boolean = false;
  isCITC: boolean = false;
  isLIHTC: boolean = false;
  isNOAH: boolean = false;
  isDeferredLoan: boolean = false;
  isVHCB: boolean = false;
  isAHP: boolean = false;
  isNSP: boolean = false;
  isKCMO: boolean = false;
  isStLouisFunds: boolean = false;
  isHTF: boolean = false;
  isTCAP: boolean = false;
  isBOND: boolean = false;

  // utility
  isNotHUDProgram: boolean = false;
  isLayered: boolean = false;
  programs: Array<string> = [];

  constructor(program?: string | string[]) {
    if (typeof program === 'string') {
      this.name = program ?? '';
      this.isLIHTC = this.name.indexOf('/') !== -1;
      if (this.name) {
        this.addProgram(this.name);
      }
    } else if (Array.isArray(program)) {
      this.name = program.join('/');
      program.forEach((entry) => {
        if (entry) {
          this.addProgram(entry);
        }
      });
    }
  }

  /**
   * Checks if any program is specified
   * @returns {boolean}
   */
  some(): boolean {
    return [
      this.isHOME,
      this.isCITC,
      this.isLIHTC,
      this.isHUD,
      this.isNOAH,
      this.isDeferredLoan,
      this.isVHCB,
      this.isAHP,
      this.isNSP,
      this.isKCMO,
      this.isStLouisFunds,
      this.isHTF,
      this.isTCAP,
      this.isBOND,
      this.isPH,
      this.isPBV,
      this.isRD,
    ].some((val) => val);
  }

  /**
   * Construct the program helper manually
   */
  addProgram(programName: string) {
    // Supposedly federal programs can't be slash programs
    // this looks odd, but to support floor plans which have multiple programs
    this.isNotHUDProgram =
      this.isNotHUDProgram || programName !== HUD_PROGRAM_NAME;

    this.isHUD = this.isHUD || programName === HUD_PROGRAM_NAME;
    this.isRD = this.isRD || programName === RD_PROGRAM_NAME;
    this.isPH = this.isPH || programName === PH_PROGRAM_NAME;
    this.isPBV = this.isPBV || programName === PBV_PROGRAM_NAME;

    // LIHTC can combine others, so we would need to check includes
    this.isHOME = this.isHOME || programName.includes(HOME_PROGRAM_NAME);
    this.isCITC = this.isCITC || programName === CITC_PROGRAM_NAME;
    this.isLIHTC = this.isLIHTC || programName.includes(LIHTC_PROGRAM_NAME);
    this.isNOAH = this.isNOAH || programName.includes(NOAH_PROGRAM_NAME);
    this.isDeferredLoan =
      this.isDeferredLoan || programName.includes(DEFERRED_LOAN_PROGRAM_NAME);
    this.isVHCB = this.isVHCB || programName === VHCB_PROGRAM_NAME;
    this.isAHP = this.isAHP || programName === AHP_PROGRAM_NAME;
    this.isNSP = this.isNSP || programName === NSP_PROGRAM_NAME;
    this.isKCMO = this.isKCMO || programName === KCMO_PROGRAM_NAME;
    this.isStLouisFunds =
      this.isStLouisFunds || programName === ST_LOUIS_FUNDS_PROGRAM_NAME;
    this.isHTF = this.isHTF || programName === HTF_PROGRAM_NAME;
    this.isTCAP = this.isTCAP || programName === TCAP_PROGRAM_NAME;
    this.isBOND = this.isBOND || programName === BOND_PROGRAM_NAME;

    // sadly not as straightforward, so for backwards compatability keep array
    // handling?
    this.programs.push(programName);
    this.isLayered = this.isLayered || this.programs.length > 1;
  }
}
